// Photo slideshow for a drive. Built on the project's embla carousel, so touch
// swiping works natively. Arrows and dots are ALWAYS visible (never hover-only)
// because most visitors are on phones, and auto-advance pauses as soon as the
// visitor interacts or the tab is hidden.

import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { cldImg } from "@/lib/img";

const AUTOPLAY_MS = 5000;

export default function DrivePhotoCarousel({ images = [], alt = "" }) {
    const [api, setApi] = useState(null);
    const [selected, setSelected] = useState(0);
    const [paused, setPaused] = useState(false);

    // Track the active slide for the dots.
    useEffect(() => {
        if (!api) return undefined;
        const onSelect = () => setSelected(api.selectedScrollSnap());
        onSelect();
        api.on("select", onSelect);
        api.on("pointerDown", () => setPaused(true));
        return () => {
            api.off("select", onSelect);
        };
    }, [api]);

    useEffect(() => {
        if (!api || paused || images.length < 2) return undefined;
        const id = setInterval(() => {
            if (!document.hidden) api.scrollNext();
        }, AUTOPLAY_MS);
        return () => clearInterval(id);
    }, [api, paused, images.length]);

    const go = useCallback(
        (fn) => {
            setPaused(true);
            fn();
        },
        [],
    );

    if (images.length === 0) return null;

    // A single photo needs no carousel chrome.
    if (images.length === 1) {
        return (
            <div className="overflow-hidden rounded-2xl border border-seedh-border bg-seedh-surface-beige/60 aspect-[16/9]">
                <img src={cldImg(images[0], 1200)} alt={alt} className="w-full h-full object-cover" />
            </div>
        );
    }

    const arrow =
        "absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 border border-seedh-border text-seedh-text flex items-center justify-center shadow-sm hover:bg-white transition-colors";

    return (
        <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <Carousel setApi={setApi} opts={{ loop: true }}>
                <CarouselContent>
                    {images.map((src, i) => (
                        <CarouselItem key={i}>
                            <div className="overflow-hidden rounded-2xl border border-seedh-border bg-seedh-surface-beige/60 aspect-[16/9]">
                                <img
                                    src={cldImg(src, 1200)}
                                    alt={alt ? `${alt} — photo ${i + 1}` : `Photo ${i + 1}`}
                                    loading={i === 0 ? "eager" : "lazy"}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
            </Carousel>

            <button
                type="button"
                onClick={() => go(() => api?.scrollPrev())}
                aria-label="Previous photo"
                className={`${arrow} left-3`}
            >
                <ChevronLeft className="w-5 h-5" />
            </button>
            <button
                type="button"
                onClick={() => go(() => api?.scrollNext())}
                aria-label="Next photo"
                className={`${arrow} right-3`}
            >
                <ChevronRight className="w-5 h-5" />
            </button>

            <div className="mt-4 flex items-center justify-center gap-2">
                {images.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => go(() => api?.scrollTo(i))}
                        aria-label={`Go to photo ${i + 1}`}
                        aria-current={selected === i}
                        className={`h-2 rounded-full transition-all ${
                            selected === i ? "w-6 bg-seedh-green" : "w-2 bg-seedh-border hover:bg-seedh-muted"
                        }`}
                    />
                ))}
            </div>
        </div>
    );
}
